import { alpha, createTheme } from '@mui/material/styles';

// Brand (see public/mangrovecollect-*.svg): leaf green for actions, the deep
// mangrove green as its dark shade, and the logo's teal-blue as secondary, on a
// soft, slightly green-tinted neutral.
const PRIMARY = '#2E713B';
const PRIMARY_LIGHT = '#4E9A5C';
const PRIMARY_DARK = '#163F30';
const SECONDARY = '#337889';
const INK = '#1f2933';
const INK_SOFT = '#52606d';
const DIVIDER = 'rgba(31, 41, 51, 0.1)';
const FONT = ['Poppins', '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'Roboto', 'sans-serif'].join(',');

const theme = createTheme({
  palette: {
    primary: { main: PRIMARY, light: PRIMARY_LIGHT, dark: PRIMARY_DARK, contrastText: '#ffffff' },
    secondary: { main: SECONDARY, light: '#5C9DAD', dark: '#245766', contrastText: '#ffffff' },
    background: { default: '#f4f7f6', paper: '#ffffff' },
    text: { primary: INK, secondary: INK_SOFT },
    divider: DIVIDER,
    error: { main: '#c62828', light: '#ef5350', dark: '#b71c1c' },
    warning: { main: '#ef6c00', light: '#ff9800', dark: '#e65100' },
    info: { main: '#0277bd', light: '#4fc3f7', dark: '#01579b' },
    success: { main: '#2e7d32', light: '#4caf50', dark: '#1b5e20' },
    // Chart tokens (validated with the data-viz palette checker on a white surface).
    chart: {
      series: [PRIMARY, '#ef6c00', '#1e88e5'],
      grid: '#e4e9e7',
      axis: '#c3cbc8',
      muted: '#78858f',
      surface: '#ffffff',
    },
    // Map tokens: survey start / end use the first two chart colours, "you are
    // here" the third; a chosen survey is outlined in the deep green.
    map: {
      start: PRIMARY,
      end: '#ef6c00',
      you: '#1e88e5',
      selected: PRIMARY_DARK,
    },
  },
  typography: {
    fontFamily: FONT,
    h1: { fontSize: '1.875rem', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.25 },
    h2: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.005em', lineHeight: 1.3 },
    h3: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.35 },
    h4: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
    h5: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.45 },
    h6: { fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.5 },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 600 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
    body2: { fontSize: '0.8438rem', lineHeight: 1.55 },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
    caption: { fontSize: '0.75rem', lineHeight: 1.5 },
    overline: { fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.1em' },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: '#f4f7f6' },
        '*:focus-visible': { outline: `2px solid ${PRIMARY_LIGHT}`, outlineOffset: 2 },
      },
    },
    // Buttons are exactly 40px, 32px small or 48px large, whatever the variant:
    // a whole-pixel text line (MUI's 1.75 gives 24.5px), and outlined buttons
    // take their 1.5px border out of the padding.
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 10, padding: '8px 18px', minHeight: 40, lineHeight: '24px' },
        sizeSmall: { padding: '4px 12px', minHeight: 32, fontSize: '0.8125rem' },
        sizeLarge: { padding: '10px 24px', minHeight: 48, fontSize: '1rem', lineHeight: '28px' },
        outlinedSizeMedium: { padding: '6.5px 16.5px' },
        outlinedSizeSmall: { padding: '2.5px 10.5px' },
        outlinedSizeLarge: { padding: '8.5px 22.5px' },
        contained: {
          '&:hover': { boxShadow: `0 6px 16px ${alpha(PRIMARY, 0.24)}` },
        },
        containedError: {
          '&:hover': { boxShadow: `0 6px 16px ${alpha('#c62828', 0.24)}` },
        },
        outlined: { borderWidth: 1.5, '&:hover': { borderWidth: 1.5 } },
      },
    },
    // Square icon buttons at the same heights as buttons: 40px, 32px small,
    // 48px large. Autocomplete's own arrow and clear buttons keep their size.
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          '&.MuiAutocomplete-popupIndicator, &.MuiAutocomplete-clearIndicator': { width: 'auto', height: 'auto' },
        },
        sizeMedium: { width: 40, height: 40 },
        sizeSmall: { width: 32, height: 32 },
        sizeLarge: { width: 48, height: 48 },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 16 },
        elevation0: { border: `1px solid ${DIVIDER}` },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { transition: 'box-shadow 200ms ease, transform 200ms ease' },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: { '&:hover .MuiCardActionArea-focusHighlight': { opacity: 0.04 } },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: '#ffffff',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(31, 41, 51, 0.2)' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY_LIGHT },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
        },
        // Every field is one of two heights, whatever kind it is (text, select,
        // date, autocomplete): 48px, or 40px with size="small". Multi-line
        // fields keep MUI's own padding.
        input: ({ ownerState }) =>
          ownerState.multiline || ownerState.size === 'small' ? {} : { paddingTop: 12.5, paddingBottom: 12.5 },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        // A whole-pixel text line (MUI's 1.4375em is 21.56px at our 15px body
        // size), so fields come out at exactly 48px and 40px, the same as the
        // buttons beside them.
        root: { lineHeight: '23px' },
        input: ({ ownerState }) => (ownerState.multiline ? {} : { height: 23 }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        // Autocomplete pads its own input; trim the normal size to 48px too
        // (the small size is already 40px).
        inputRoot: ({ ownerState }) => (ownerState.size === 'small' ? {} : { paddingTop: 5, paddingBottom: 5 }),
        // A multi-select stays one line (and the field's height) while it isn't
        // being edited: chips are cut short and the rest shows as "+N". While
        // focused it may wrap to show every choice.
        root: {
          '&:not(.Mui-focused) .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap', overflow: 'hidden' },
          '&:not(.Mui-focused) .MuiAutocomplete-tag': { minWidth: 0, flexShrink: 1 },
          '& .MuiAutocomplete-tag': { maxWidth: 'calc(100% - 56px)' },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          fontWeight: 500,
          // Centre the resting label in the 48px field (MUI places it for 56px).
          ...(ownerState.variant === 'outlined' && !ownerState.shrink && ownerState.size !== 'small'
            ? { transform: 'translate(14px, 12px) scale(1)' }
            : {}),
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: { root: { marginLeft: 2 } },
    },
    // Chips (dates, counts, roles, statuses) are all one size: 24px, 13px
    // label, 8px sides, 16px icons. size="medium" (32px) exists but isn't
    // used. Don't set chip heights, padding or font sizes in pages.
    MuiChip: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: { fontWeight: 500, borderRadius: 8 },
        sizeSmall: { height: 24, fontSize: '0.8125rem' },
        labelSmall: { paddingLeft: 8, paddingRight: 8 },
        iconSmall: { fontSize: 16, marginLeft: 6, marginRight: -2 },
        // MUI pulls an outlined chip's icon to 2px from the edge; keep 6px.
        outlined: { '& .MuiChip-iconSmall': { marginLeft: 6 } },
      },
    },
    // Dialogs are spaced like panels: 20px inside on phones, 24px wider, with
    // the title, content and actions on one edge. On phones they keep a 16px
    // margin rather than MUI's 32px, so forms get the width.
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme: t }) => ({ borderRadius: 16, margin: 16, [t.breakpoints.up('sm')]: { margin: 32 } }),
        paperFullWidth: ({ theme: t }) => ({ width: 'calc(100% - 32px)', [t.breakpoints.up('sm')]: { width: 'calc(100% - 64px)' } }),
        paperFullScreen: ({ theme: t }) => ({ margin: 0, width: '100%', borderRadius: 0, [t.breakpoints.up('sm')]: { margin: 0, width: '100%' } }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          fontSize: '1.125rem',
          fontWeight: 600,
          padding: '20px 20px 8px',
          [t.breakpoints.up('md')]: { padding: '24px 24px 8px' },
        }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          padding: '8px 20px',
          [t.breakpoints.up('md')]: { padding: '8px 24px' },
          // MUI drops the top padding under a title, which clips the floating
          // label of a first field; keep 8px instead.
          '.MuiDialogTitle-root + &': { paddingTop: 8 },
        }),
        dividers: ({ theme: t }) => ({
          marginTop: 8,
          padding: '16px 20px',
          [t.breakpoints.up('md')]: { padding: '16px 24px' },
          '.MuiDialogTitle-root + &': { paddingTop: 16 },
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          padding: '16px 20px 20px',
          [t.breakpoints.up('md')]: { padding: '16px 24px 24px' },
        }),
      },
    },
    MuiTabs: {
      styleOverrides: { indicator: { height: 3, borderRadius: '3px 3px 0 0' } },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, minHeight: 48, '&.Mui-selected': { fontWeight: 600 } },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: { '&:hover': { backgroundColor: alpha(PRIMARY, 0.04) } },
      },
    },
    // Tooltips name what an icon-only control does (IconAction gives every
    // icon button one). Short delay so they don't flash while the pointer
    // passes; moving between neighbours shows the next one quickly.
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: INK, fontSize: '0.75rem', borderRadius: 6, padding: '6px 10px' },
        arrow: { color: INK },
      },
      defaultProps: { arrow: true, enterDelay: 400, enterNextDelay: 150 },
    },
    // A count on an icon (the sync icon): the same size and type as CountBadge.
    MuiBadge: {
      styleOverrides: {
        badge: {
          height: 20,
          minWidth: 20,
          padding: '0 6px',
          borderRadius: 10,
          fontSize: '0.75rem',
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
        },
      },
    },
    // One table density everywhere: compact rows (10px above and below,
    // 16px at the sides). A table that fills its card edge to edge adds
    // FULL_BLEED_TABLE (utils/layout) so its outer columns line up with lists.
    MuiTable: {
      defaultProps: { size: 'small' },
    },
    MuiTableCell: {
      styleOverrides: {
        sizeSmall: { padding: '10px 16px' },
        head: {
          backgroundColor: '#f7f9f8',
          color: INK_SOFT,
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        },
        root: { borderBottomColor: DIVIDER },
      },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 10 } },
    },
    MuiLink: {
      defaultProps: { underline: 'hover' },
    },
    // Toggle groups usually sit beside fields, so they match field heights:
    // 48px, or 40px small. Labels stay on one line.
    MuiToggleButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, lineHeight: '22px', whiteSpace: 'nowrap' },
        sizeMedium: { height: 48 },
        sizeSmall: { height: 40, paddingTop: 0, paddingBottom: 0 },
      },
    },
    // Avatars are one size: 32px with 13px initials (it fits a 40px icon
    // button, as in the top bar). Don't size avatars in pages.
    MuiAvatar: {
      styleOverrides: { root: { fontWeight: 600, width: 32, height: 32, fontSize: '0.8125rem' } },
    },
    // Dropdowns (menus and selects) don't lock page scrolling. Locking hides
    // the page scrollbar while one is open, which makes the page jolt; that's
    // only worth it for dialogs, which cover the whole page anyway.
    //
    // Menus and popovers are one kind of floating card: 12px corners, a 1px
    // border, 6px below the button that opened them, lined up with its right
    // edge. Items have a 20px icon and a 15px label (optionally a grey second
    // line); groups get an uppercase subheader like the table headers.
    MuiMenu: {
      defaultProps: {
        disableScrollLock: true,
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        transformOrigin: { vertical: 'top', horizontal: 'right' },
      },
      styleOverrides: {
        paper: { minWidth: 220 },
      },
    },
    MuiPopover: {
      defaultProps: { disableScrollLock: true },
      styleOverrides: {
        paper: { borderRadius: 12, marginTop: 6, border: `1px solid ${DIVIDER}` },
      },
    },
    MuiMenuItem: {
      styleOverrides: { root: { borderRadius: 8, margin: '2px 6px', paddingTop: 8, paddingBottom: 8 } },
    },
    MuiListItemIcon: {
      styleOverrides: { root: { minWidth: 36, color: INK_SOFT } },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          lineHeight: 1.5,
          padding: '10px 16px 4px',
          fontSize: '0.75rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: INK_SOFT,
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: { root: { borderRadius: 8 } },
    },
  },
});

export default theme;
